import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Plus, Activity, Target, Thermometer, MapPin, Edit, ChevronDown } from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { firingSessionsAPI, firearmsAPI, loadsAPI, uploadsAPI } from '../services/api';
import ImagePreviewModal from '../components/ImagePreviewModal';
import PdfThumbnail from '../components/PdfThumbnail';
import { formatLocalDate } from '../services/date';

// Simple Web Audio helpers for sound effects
let sharedAudioContext;
const getAudioContext = () => {
  if (!sharedAudioContext) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) sharedAudioContext = new Ctx();
  }
  return sharedAudioContext;
};

const createNoiseBuffer = (ctx, durationSec = 0.2) => {
  const sampleRate = ctx.sampleRate;
  const length = Math.floor(sampleRate * durationSec);
  const buffer = ctx.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / length); // simple decay
  }
  return buffer;
};

const playGunshot = () => {
  const ctx = getAudioContext();
  if (!ctx) return;
  // In case context is suspended (user gesture), attempt resume
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});

  const now = ctx.currentTime;
  const noise = ctx.createBufferSource();
  noise.buffer = createNoiseBuffer(ctx, 0.25);

  const bandpass = ctx.createBiquadFilter();
  bandpass.type = 'bandpass';
  bandpass.frequency.setValueAtTime(1500, now);
  bandpass.Q.setValueAtTime(0.8, now);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.9, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

  noise.connect(bandpass).connect(gain).connect(ctx.destination);
  noise.start(now);
  noise.stop(now + 0.4);
};

const playShotgunPump = () => {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});

  const now = ctx.currentTime;
  const makeChunk = (start, freq = 800, dur = 0.09) => {
    const noise = ctx.createBufferSource();
    noise.buffer = createNoiseBuffer(ctx, dur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(freq, start);
    bp.Q.setValueAtTime(2.0, start);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.6, start);
    g.gain.linearRampToValueAtTime(0.0, start + dur);
    noise.connect(bp).connect(g).connect(ctx.destination);
    noise.start(start);
    noise.stop(start + dur + 0.02);
  };
  // Two sliding chunks to mimic pump action
  makeChunk(now, 1200, 0.08);
  makeChunk(now + 0.11, 600, 0.10);
};

const FiringSessions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const formRef = useRef(null);
  const [firearms, setFirearms] = useState([]);
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [imagesBySession, setImagesBySession] = useState({});
  const [filePreview, setFilePreview] = useState(null);
  const [createSessionFiles, setCreateSessionFiles] = useState([]);
  const [createSessionPreviews, setCreateSessionPreviews] = useState([]);
  const [importingVelocities, setImportingVelocities] = useState(false);
  const importVelocityInputRef = useRef(null);
  const pendingSessionIdRef = useRef(null);
  const [groupBy, setGroupBy] = useState('date');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: {
      velocities: []
    }
  });
  
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'velocities'
  });

  const watchedVelocities = watch('velocities');
  const hasVelocities = (watchedVelocities || []).length > 0;
  const enteredVelocityCount = (watchedVelocities || []).reduce((count, entry) => {
    const num = Number(entry?.velocity);
    return Number.isFinite(num) ? count + 1 : count;
  }, 0);
  const allowZeroDeletion = Boolean(editingSession);
  const minRoundsRequired = Math.max(1, enteredVelocityCount);
  const inputMin = allowZeroDeletion ? 0 : minRoundsRequired;

  const watchedLoadId = watch('load_id');
  const selectedLoad = loads.find((load) => String(load.id) === String(watchedLoadId));
  const selectedLoadRemaining = selectedLoad && Number.isFinite(Number(selectedLoad.remaining_quantity))
    ? Number(selectedLoad.remaining_quantity)
    : null;

  let maxRoundsAllowed = selectedLoadRemaining;
  if (editingSession) {
    const baseline = Number(editingSession.rounds_fired || 0);
    const editingLoad = editingSession.load ? loads.find((l) => l.id === editingSession.load.id) : null;
    const editingRemaining = editingLoad && Number.isFinite(Number(editingLoad.remaining_quantity))
      ? Number(editingLoad.remaining_quantity)
      : null;
    const remainingForSelected = Number.isFinite(selectedLoadRemaining) ? selectedLoadRemaining : null;
    const usableRemaining = Number.isFinite(remainingForSelected)
      ? remainingForSelected
      : (Number.isFinite(editingRemaining) ? editingRemaining : 0);
    if (Number.isFinite(usableRemaining)) {
      maxRoundsAllowed = baseline + usableRemaining;
    } else if (Number.isFinite(baseline)) {
      maxRoundsAllowed = baseline;
    }
  }

  const hasMaxLimit = Number.isFinite(maxRoundsAllowed);
  const placeholderMinText = allowZeroDeletion
    ? `${minRoundsRequired} (0 deletes session)`
    : `${minRoundsRequired}`;
  const roundsFiredPlaceholder = hasMaxLimit
    ? `Min ${placeholderMinText} • Max ${maxRoundsAllowed}`
    : `Min ${placeholderMinText}`;

  const addVelocityField = () => {
    append({ velocity: '' });
  };

  const removeVelocityField = (index) => {
    remove(index);
  };

  const handleClearVelocities = () => {
    replace([]);
    toast.success('Velocities cleared');
  };

  const applyImportedVelocities = (values) => {
    if (!Array.isArray(values) || values.length === 0) {
      return 0;
    }
    const sanitized = values
      .map((value) => {
        const num = Number(value);
        if (!Number.isFinite(num)) return null;
        return { velocity: `${Math.round(num)}` };
      })
      .filter(Boolean);
    if (sanitized.length === 0) {
      return 0;
    }
    const current = getValues('velocities') || [];
    replace([...current, ...sanitized]);
    return sanitized.length;
  };

  const handleVelocityImport = async (files) => {
    const importFiles = Array.from(files || []).filter(Boolean);
    if (importFiles.length === 0) return;
    setImportingVelocities(true);
    let totalImported = 0;
    let hadError = false;
    for (const file of importFiles) {
      try {
        const res = await firingSessionsAPI.importVelocities(file);
        const parsed = Array.isArray(res?.data?.velocities) ? res.data.velocities : [];
        totalImported += applyImportedVelocities(parsed);
      } catch (error) {
        hadError = true;
        console.error('Failed to import velocities', error);
        const message = error?.response?.data?.error || `Failed to import ${file.name}`;
        toast.error(message);
      }
    }
    // Show success message if any velocities were imported (even if some files failed)
    if (totalImported > 0) {
      toast.success(`Imported ${totalImported} velocities`);
    } else if (!hadError) {
      // Only show "no velocities found" if there were no errors (all files processed but none had data)
      toast.error('No velocities were found under SPEED (FPS).');
    }
    setImportingVelocities(false);
  };

  const handleVelocityImportChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    await handleVelocityImport(files);
    if (editingSession && editingSession.id) {
      await handleUploadSession(editingSession.id, files);
    } else {
      setCreateSessionFiles((prev) => [...prev, ...files]);
    }
  };

  const groupedSessions = useMemo(() => {
    if (!Array.isArray(sessions) || sessions.length === 0) return [];
    if (groupBy === 'recipe') {
      const map = new Map();
      sessions.forEach((session) => {
        const recipeId = session.load?.recipe?.id ?? `load-${session.load?.id ?? 'unknown'}`;
        const key = `recipe-${recipeId}`;
        const title = session.load?.recipe?.name || session.load?.recipe_name || 'Unknown Recipe';
        const subtitle = session.load?.recipe?.cartridge || session.load?.recipe?.cartridge_other || null;
        if (!map.has(key)) {
          map.set(key, { key, title, subtitle, sessions: [] });
        }
        map.get(key).sessions.push(session);
      });
      const entries = Array.from(map.values());
      entries.forEach((entry) => {
        entry.sessions.sort((a, b) => new Date(b.session_date || 0) - new Date(a.session_date || 0));
      });
      entries.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
      return entries;
    }

    const map = new Map();
    sessions.forEach((session) => {
      const dateObj = session.session_date ? new Date(session.session_date) : null;
      const iso = dateObj ? dateObj.toISOString().split('T')[0] : 'unknown';
      const key = `date-${iso}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          rawDate: dateObj ? iso : null,
          title: dateObj ? formatLocalDate(session.session_date) : 'Unknown Date',
          subtitle: null,
          sessions: [],
        });
      }
      map.get(key).sessions.push(session);
    });
    const entries = Array.from(map.values());
    entries.forEach((entry) => {
      entry.sessions.sort((a, b) => new Date(b.session_date || 0) - new Date(a.session_date || 0));
    });
    entries.sort((a, b) => {
      const aDate = a.rawDate ? new Date(a.rawDate) : null;
      const bDate = b.rawDate ? new Date(b.rawDate) : null;
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return bDate - aDate;
    });
    return entries;
  }, [sessions, groupBy]);

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? true),
    }));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const sessionId = params.get('sessionId');
    if (sessionId) {
      pendingSessionIdRef.current = sessionId;
    }
  }, [location.search]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sessionsRes, firearmsRes, loadsRes] = await Promise.all([
        firingSessionsAPI.getAll(),
        firearmsAPI.getAll(),
        loadsAPI.getAll()
      ]);
      console.log('Loaded data:', {
        sessions: sessionsRes.data.length,
        firearms: firearmsRes.data.length,
        loads: loadsRes.data.length,
        loadsData: loadsRes.data
      });
      if (loadsRes.data.length > 0) {
        const firstLoad = loadsRes.data[0];
        console.log('Load cartridge test:', {
          id: firstLoad.id,
          cartridge: firstLoad.recipe.cartridge,
          cartridge_other: firstLoad.recipe.cartridge_other
        });
      }
      setSessions(sessionsRes.data);
      // Handle deep link to edit a specific session once data is available
      if (pendingSessionIdRef.current) {
        const match = sessionsRes.data.find(s => String(s.id) === String(pendingSessionIdRef.current));
        if (match) {
          handleEdit(match);
          pendingSessionIdRef.current = null;
          navigate('/firing-sessions', { replace: true });
        }
      }
      // Load images for sessions
      try {
        const results = await Promise.all(
          sessionsRes.data.map(s => uploadsAPI.list('firing_session', s.id).then(r => ({ id: s.id, images: r.data })).catch(() => ({ id: s.id, images: [] })))
        );
        const map = {};
        results.forEach(r => { map[r.id] = r.images; });
        setImagesBySession(map);
      } catch {}
      setFirearms(firearmsRes.data);
      setLoads(loadsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSession = async (sessionId, files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    try {
      await uploadsAPI.upload('firing_session', sessionId, arr);
      const res = await uploadsAPI.list('firing_session', sessionId);
      setImagesBySession(prev => ({ ...prev, [sessionId]: res.data }));
      toast.success('Images uploaded');
      // Notify Recipes page to refresh images
      try {
        const session = sessions.find(s => String(s.id) === String(sessionId));
        const loadId = session?.load?.id || session?.load_id;
        const load = loads.find(l => String(l.id) === String(loadId));
        const recipeId = load?.recipe?.id || load?.recipe_id;
        if (recipeId) {
          window.dispatchEvent(new CustomEvent('uploadsChanged', {
            detail: { entity: 'firing_session', id: sessionId, recipeId }
          }));
        }
      } catch {}
    } catch {
      toast.error('Failed to upload images');
    }
  };

  const removeSessionImage = async (sessionId, imageId) => {
    try {
      await uploadsAPI.remove(imageId);
      setImagesBySession(prev => ({ ...prev, [sessionId]: (prev[sessionId] || []).filter(i => i.id !== imageId) }));
      toast.success('Image deleted');
    } catch {
      toast.error('Failed to delete image');
    }
  };

  const onSubmit = async (data) => {
    try {
      const velocities = data.velocities
        .map(v => v.velocity ? parseFloat(v.velocity) : null)
        .filter(v => v !== null);

      const parsedRounds = parseInt(data.rounds_fired, 10);
      const loadForEvent = loads.find((l) => String(l.id) === String(data.load_id));
      const loadRecipeId = loadForEvent?.recipe?.id ?? null;

      if (editingSession) {
        const res = await firingSessionsAPI.update(editingSession.id, {
          ...data,
          rounds_fired: parsedRounds,
          temperature: data.temperature ? parseFloat(data.temperature) : null,
          velocities
        });
        const deleted = Boolean(res?.data?.deleted);
        const recipeIdFromRes = res?.data?.recipe_id ?? loadRecipeId ?? editingSession?.load?.recipe?.id ?? null;
        if (deleted) {
          toast.success('Firing session deleted');
        } else {
          playGunshot();
          toast.success('Firing session updated successfully');
        }
        window.dispatchEvent(new CustomEvent('firingSessionChanged', {
          detail: {
            action: deleted ? 'deleted' : 'updated',
            sessionId: editingSession.id,
            loadId: data.load_id,
            recipeId: recipeIdFromRes
          }
        }));
        setEditingSession(null);
      } else {
        const res = await firingSessionsAPI.create({
          ...data,
          rounds_fired: parsedRounds,
          temperature: data.temperature ? parseFloat(data.temperature) : null,
          velocities
        });
        // Upload any files selected via the Upload Files button for new session
        try {
          const sid = res?.data?.id;
          if (sid && Array.isArray(createSessionFiles) && createSessionFiles.length > 0) {
            await uploadsAPI.upload('firing_session', sid, Array.from(createSessionFiles));
            const list = await uploadsAPI.list('firing_session', sid);
            setImagesBySession(prev => ({ ...prev, [sid]: Array.isArray(list?.data) ? list.data : [] }));
            // Notify Recipes page to refresh images
            try {
              window.dispatchEvent(new CustomEvent('uploadsChanged', {
                detail: { entity: 'firing_session', id: sid, recipeId: loadRecipeId }
              }));
            } catch {}
          }
        } catch {}
        playGunshot();
        toast.success('Firing session recorded successfully');
        window.dispatchEvent(new CustomEvent('firingSessionChanged', {
          detail: {
            action: 'created',
            sessionId: res?.data?.id,
            loadId: data.load_id,
            recipeId: loadRecipeId
          }
        }));
      }
      
      setShowForm(false);
      reset();
      setCreateSessionFiles([]);
      // Incremental update: fetch only sessions and loads instead of full loadData()
      const [sessionsRes, loadsRes] = await Promise.all([
        firingSessionsAPI.getAll(),
        loadsAPI.getAll()
      ]);
      setSessions(Array.isArray(sessionsRes.data) ? sessionsRes.data : []);
      setLoads(Array.isArray(loadsRes.data) ? loadsRes.data : []);
    } catch (error) {
      console.error('Error saving firing session:', error);
      toast.error(editingSession ? 'Failed to update firing session' : 'Failed to record firing session');
    }
  };

  const handleEdit = (session) => {
    setEditingSession(session);
    try {
      setValue('load_id', session.load?.id ? String(session.load.id) : '');
    } catch {}
    const sortedVelocityRecords = session.velocity_records?.sort((a, b) => a.shot_number - b.shot_number) || [];
    const velocityFields = sortedVelocityRecords.map(vr => ({ velocity: vr.velocity?.toString() || '' }));
    reset({
      firearm_id: session.firearm.id.toString(),
      load_id: session.load.id.toString(),
      rounds_fired: session.rounds_fired.toString(),
      session_date: session.session_date,
      location: session.location || '',
      notes: session.notes || '',
      temperature: session.temperature?.toString() || '',
      velocities: velocityFields
    });
    setShowForm(true);
    requestAnimationFrame(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSession(null);
    reset();
  };

  const renderSessionCard = (session) => (
    <div key={session.id} className="card p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <div className="p-3 bg-orange-100 rounded-full">
            <Target className="h-6 w-6 text-orange-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="font-semibold text-gray-900">
                {session.load?.recipe_name || 'Unknown Recipe'}
              </h3>
              {typeof session.load?.firing_count !== 'undefined' && (
                <span className="text-sm text-gray-600">
                  {session.load.firing_count != null ? `${session.load.firing_count}x fired brass` : 'new brass'}
                </span>
              )}
            </div>

            <p className="text-sm text-gray-600 mb-3">
              {session.firearm ? `${session.firearm.manufacturer} ${session.firearm.model}` : 'Unknown Firearm'}
              {session.firearm?.caliber && ` • ${session.firearm.caliber}`}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
              <div className="flex items-center space-x-2">
                <Activity className="h-4 w-4 text-gray-400" />
                <span>{session.rounds_fired} rounds</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-gray-400" />
                <span>{session.location}</span>
              </div>
              {session.temperature && (
                <div className="flex items-center space-x-2">
                  <Thermometer className="h-4 w-4 text-gray-400" />
                  <span>{session.temperature}°F</span>
                </div>
              )}
              <div className="text-gray-600">
                {formatLocalDate(session.session_date)}
              </div>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg mb-3">
              <h5 className="font-medium text-gray-900 mb-2">Velocity Statistics</h5>
              <VelocityStats stats={session.velocity_statistics} />
            </div>

            {session.notes && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">{session.notes}</p>
              </div>
            )}
            <div className="mt-3">
              <div className="flex items-center gap-2 mb-2">
                <label className="btn btn-secondary btn-sm cursor-pointer">
                  Upload Files
                  <input
                    type="file"
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log"
                    multiple
                    className="hidden"
                    onChange={(e) => handleUploadSession(session.id, e.target.files)}
                  />
                </label>
              </div>
              <div className="flex gap-2 overflow-x-auto pt-2 pb-1">
                {(imagesBySession[session.id] || []).map(img => {
                  const src = img.download_url || img.url;
                  const isPdf = (img.filename || '').toLowerCase().endsWith('.pdf') || (src || '').toLowerCase().endsWith('.pdf');
                  return (
                    <div key={img.id} className="relative group flex-shrink-0 first:ml-0 last:mr-2">
                      {isPdf ? (
                        <PdfThumbnail
                          fileId={img.id}
                          url={src}
                          thumbnailUrl={img.thumbnail_url}
                          filename={img.filename}
                          sizeClass="w-16 h-16"
                          onClick={() => setFilePreview({ id: img.id, url: src, filename: img.filename, thumbnailUrl: img.thumbnail_url })}
                        />
                      ) : (
                        (() => {
                          const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((img.filename || ''));
                          const thumb = isImg ? src : (img.thumbnail_url || src);
                          return (
                            <img
                              src={thumb}
                              alt={img.filename}
                              className="w-16 h-16 object-cover rounded shadow cursor-pointer"
                              onClick={() => setFilePreview({ id: img.id, url: src, filename: img.filename, thumbnailUrl: img.thumbnail_url })}
                            />
                          );
                        })()
                      )}
                      <button
                        className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full px-1.5 text-xs opacity-0 group-hover:opacity-100"
                        onClick={() => removeSessionImage(session.id, img.id)}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <button
          onClick={() => handleEdit(session)}
          className="btn btn-secondary btn-sm"
          title="Edit"
        >
          <Edit className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
  // Build previews for files selected in the create form
  useEffect(() => {
    let cancelled = false;
    const build = async () => {
      try {
        const files = Array.from(createSessionFiles || []);
        const results = await Promise.all(files.map(async (f) => {
          const name = f.name || '';
          const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
          const isPdf = /\.pdf$/i.test(name);
          const isDocLike = /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf|txt|csv|json|md|log)$/i.test(name);
          if (isImage || isPdf) {
            const url = URL.createObjectURL(f);
            return { url, filename: name, file: f, docThumb: false, revoke: true };
          }
          if (isDocLike) {
            try {
              const res = await uploadsAPI.thumbnailPreview(f);
              const blob = res?.data instanceof Blob ? res.data : new Blob([res.data], { type: 'image/png' });
              const url = URL.createObjectURL(blob);
              return { url, filename: name, file: f, docThumb: true, revoke: true };
            } catch {
              const url = URL.createObjectURL(f);
              return { url, filename: name, file: f, docThumb: false, revoke: true };
            }
          }
          const url = URL.createObjectURL(f);
          return { url, filename: name, file: f, docThumb: false, revoke: true };
        }));
        if (!cancelled) setCreateSessionPreviews(results);
      } catch {
        if (!cancelled) setCreateSessionPreviews([]);
      }
    };
    build();
    return () => {
      cancelled = true;
      try {
        (createSessionPreviews || []).forEach(p => { if (p && p.revoke && p.url) URL.revokeObjectURL(p.url); });
      } catch {}
    };
  }, [createSessionFiles]);
  const removeCreateSessionPreview = (idx) => setCreateSessionFiles(prev => prev.filter((_, i) => i !== idx));

  const editingLoadOption = useMemo(() => {
    if (!editingSession || !editingSession.load) return null;
    const loadId = editingSession.load.id;
    const direct = loads.find(l => l.id === loadId);
    if (direct) return direct;
    const grouped = loads.find(l => Array.isArray(l.batch_ids) && l.batch_ids.includes(loadId));
    if (grouped) return grouped;
    return {
      id: loadId,
      recipe: editingSession.load.recipe
        ? editingSession.load.recipe
        : {
            id: editingSession.load.recipe_id || null,
            name: editingSession.load.recipe_name || `Load ${loadId}`,
            cartridge: editingSession.load.recipe_cartridge || null,
            cartridge_other: editingSession.load.recipe_cartridge_other || null,
            firearm: editingSession.load.recipe?.firearm || null,
          },
      case_batch: editingSession.load.firing_count != null ? { firing_count: editingSession.load.firing_count } : null,
      remaining_quantity: editingSession.load.remaining_quantity ?? editingSession.rounds_fired ?? 0,
      quantity_created: editingSession.load.quantity_created ?? editingSession.rounds_fired ?? 0,
      total_cost: editingSession.load.quantity_created && (editingSession.load.stored_cost_per_round ?? editingSession.load.average_cost_per_round)
        ? (editingSession.load.stored_cost_per_round ?? editingSession.load.average_cost_per_round) * editingSession.load.quantity_created
        : undefined,
      cost_per_round: editingSession.load.stored_cost_per_round ?? editingSession.load.average_cost_per_round ?? 0,
      batch_ids: [loadId],
    };
  }, [editingSession, loads]);

  useEffect(() => {
    if (editingLoadOption) {
      try {
        setValue('load_id', String(editingLoadOption.id));
      } catch {}
    }
  }, [editingLoadOption, setValue]);

  const getAvailableLoads = (firearmId) => {
    if (editingLoadOption) {
      return [editingLoadOption];
    }
    if (!firearmId) return [];
    const selectedFirearm = firearms.find(f => f.id === parseInt(firearmId));
    if (!selectedFirearm) return [];
    const availableLoads = loads.filter(load => {
      if (load.remaining_quantity <= 0) return false;
      if (load.recipe.firearm && load.recipe.firearm.id === parseInt(firearmId)) return true;
      if (!load.recipe.firearm || !load.recipe.firearm.id) {
        const recipeCartridge = load.recipe.cartridge || load.recipe.cartridge_other;
        if (recipeCartridge && recipeCartridge.toLowerCase() === selectedFirearm.caliber.toLowerCase()) return true;
        if (selectedFirearm.intended_calibers) {
          try {
            const intendedCalibers = JSON.parse(selectedFirearm.intended_calibers);
            if (recipeCartridge && intendedCalibers.some(caliber => caliber.toLowerCase() === recipeCartridge.toLowerCase())) return true;
          } catch (error) {
            console.error('Error parsing intended_calibers:', error);
          }
        }
      }
      return false;
    });
    return availableLoads;
  };

  const VelocityStats = ({ stats }) => {
    if (!stats || stats.count === 0) return <span className="text-gray-500">No velocity data</span>;
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div>
          <span className="text-gray-600">Avg:</span>
          <p className="font-medium">{stats.average} fps</p>
        </div>
        <div>
          <span className="text-gray-600">ES:</span>
          <p className="font-medium">{stats.extreme_spread} fps</p>
        </div>
        <div>
          <span className="text-gray-600">SD:</span>
          <p className="font-medium">{stats.standard_deviation}</p>
        </div>
        <div>
          <span className="text-gray-600">Count:</span>
          <p className="font-medium">{stats.count}</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Firing Sessions</h1>
          <p className="text-gray-600 mt-2">Track your range sessions and performance data</p>
        </div>
        <button
          onClick={() => { playShotgunPump(); setShowForm(true); }}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Record Session</span>
        </button>
      </div>

      {/* Add/Edit Firing Session Form */}
      {showForm && (
        <div className="card p-6" ref={formRef}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingSession ? 'Edit Firing Session' : 'Record Firing Session'}
            </h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          
          {/* Data Status */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              Data loaded: {firearms.length} firearms, {loads.length} loads total
            </p>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Firearm</label>
                <select
                  {...register('firearm_id', { required: 'Firearm is required' })}
                  className="input"
                >
                  <option value="">Select firearm...</option>
                  {firearms.map(firearm => (
                    <option key={firearm.id} value={firearm.id}>
                      {firearm.manufacturer} {firearm.model} ({firearm.caliber})
                    </option>
                  ))}
                </select>
                {errors.firearm_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.firearm_id.message}</p>
                )}
                {firearms.length === 0 && (
                  <p className="text-amber-600 text-sm mt-1">
                    No firearms found. <a href="/firearms" className="text-blue-600 hover:text-blue-800 underline">Add firearms first</a>.
                  </p>
                )}
              </div>

              <div>
                <label className="label">Load</label>
                <select
                  {...register('load_id', { required: 'Load is required' })}
                  className="input"
                >
                  <option value="">Select load...</option>
                  {getAvailableLoads(watch('firearm_id')).map(load => {
                    const fc = (load.case_batch && typeof load.case_batch.firing_count !== 'undefined') ? `${load.case_batch.firing_count}x` : 'new brass';
                    const avg = (() => {
                      const tc = Number(load.total_cost);
                      const qty = Number(load.quantity_created);
                      if (!isNaN(tc) && !isNaN(qty) && qty > 0) return (tc / qty);
                      const c = (typeof load.computed_cost_per_round === 'number') ? load.computed_cost_per_round : load.cost_per_round;
                      return Number(c || 0);
                    })();
                    return (
                    <option key={load.id} value={load.id}>
                        {load.recipe.name} • {fc} • ${avg.toFixed(2)}/rd • {load.remaining_quantity} rem
                    </option>
                    );
                  })}
                </select>
                {errors.load_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.load_id.message}</p>
                )}
                {getAvailableLoads(watch('firearm_id')).length === 0 && watch('firearm_id') && (
                  <p className="text-amber-600 text-sm mt-1">
                    No loads available for this firearm. <a href="/loads" className="text-blue-600 hover:text-blue-800 underline">Create loads first</a>.
                  </p>
                )}
              </div>
              </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Rounds Fired</label>
                <input
                  type="number"
                  className="input"
                  min={inputMin}
                  max={hasMaxLimit ? maxRoundsAllowed : undefined}
                  placeholder={roundsFiredPlaceholder}
                  {...register('rounds_fired', {
                    required: 'Rounds fired is required',
                    validate: (value) => {
                      const num = Number(value);
                      if (!Number.isFinite(num)) return 'Enter a valid number';
                      if (editingSession && num === 0) {
                        return true;
                      }
                      if (num < minRoundsRequired) {
                        return `Must be at least ${minRoundsRequired} to cover entered velocities`;
                      }
                      if (hasMaxLimit && num > maxRoundsAllowed) {
                        return `Cannot exceed ${maxRoundsAllowed} remaining rounds`;
                      }
                      return true;
                    },
                  })}
                />
                {errors.rounds_fired && (
                  <p className="text-red-500 text-sm mt-1">{errors.rounds_fired.message}</p>
                )}
                {editingSession && (
                  <p className="text-xs text-amber-600 mt-1">
                    Enter 0 to delete this firing session and return all rounds to the selected load.
                  </p>
                )}
              </div>

              <div>
                <label className="label">Date</label>
                <input
                  type="date"
                  {...register('session_date', { required: 'Date is required' })}
                  className="input"
                />
                {errors.session_date && (
                  <p className="text-red-500 text-sm mt-1">{errors.session_date.message}</p>
                )}
              </div>

              <div>
                <label className="label">Temperature (°F)</label>
                <input
                  type="number"
                  {...register('temperature')}
                  className="input"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div>
              <label className="label">Location</label>
              <input
                type="text"
                {...register('location')}
                className="input"
                placeholder="Range name, coordinates, etc."
              />
            </div>

            {/* Velocity Records */}
              <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <label className="label">Velocity Records (fps)</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={addVelocityField}
                    className="btn btn-sm btn-primary"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Velocity
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => importVelocityInputRef.current?.click()}
                    disabled={importingVelocities}
                  >
                    {importingVelocities ? 'Importing…' : 'Import Velocities'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={handleClearVelocities}
                    disabled={!hasVelocities}
                  >
                    Clear Velocities
                  </button>
                  <input
                    type="file"
                    ref={importVelocityInputRef}
                    className="hidden"
                    multiple
                    accept=".csv,.xlsx"
                    onChange={handleVelocityImportChange}
                  />
                </div>
                </div>

              {fields.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
                  {fields.map((field, index) => (
                    <div key={field.id} className="relative">
                      <input
                        type="number"
                        {...register(`velocities.${index}.velocity`)}
                        className="input text-sm pr-8"
                        placeholder={`Shot ${index + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeVelocityField(index)}
                        className="absolute right-1 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700"
                        title="Remove this velocity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
              </div>
            )}
              
              <p className="text-xs text-gray-500">
                Click "Add Velocity" to record individual shots or use "Import Velocities" with a CSV/XLSX that has a SPEED (FPS) column.
              </p>
            </div>

            <div>
              <label className="label">Notes</label>
              <textarea
                {...register('notes')}
                className="input"
                rows="3"
                placeholder="Range conditions, accuracy observations, equipment used, etc."
              />
            </div>
            {!editingSession && (
              <div>
                <label className="btn btn-secondary btn-sm cursor-pointer">
                  Upload Files
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log"
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        setCreateSessionFiles((prev) => [...prev, ...files]);
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
                {createSessionFiles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{createSessionFiles.length} file(s) selected. They will upload after creation.</p>
                )}
                {createSessionPreviews.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {createSessionPreviews.map((p, idx) => {
                      const name = p.filename || '';
                      const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
                      const isPdf = /\.pdf$/i.test(name);
                      const ext = (name.split('.').pop() || '').toUpperCase();
                      return (
                        <div key={idx} className="relative group">
                          {isImage ? (
                            <img
                              src={p.url}
                              alt={name}
                              className="w-16 h-16 object-contain bg-white rounded shadow cursor-pointer"
                              onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                            />
                          ) : isPdf ? (
                            <PdfThumbnail
                              blob={p.file}
                              url={p.url}
                              filename={name}
                              sizeClass="w-16 h-16"
                              onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                            />
                          ) : (
                            <div
                              className="w-16 h-16 bg-gray-100 border rounded shadow flex items-center justify-center cursor-pointer overflow-hidden"
                              onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                              title={name}
                            >
                              {p.docThumb ? (
                                <img src={p.url} alt={name} className="w-full h-full object-contain" />
                              ) : (
                                <span className="text-[10px] text-gray-700 font-medium">{ext || 'FILE'}</span>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full px-1.5 text-xs opacity-0 group-hover:opacity-100"
                            onClick={() => removeCreateSessionPreview(idx)}
                            title="Remove"
                          >×</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
              

            <div className="flex space-x-3">
              <button type="submit" className="btn btn-primary">
                {editingSession ? 'Update Session' : 'Record Session'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
          {/* Session Images (after save, attach to session by selecting from list after create/update) */}
          {editingSession && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-2">
                <label className="btn btn-secondary btn-sm cursor-pointer">
                  Upload Files
                  <input type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log" multiple className="hidden" onChange={(e) => handleUploadSession(editingSession.id, e.target.files)} />
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                {(imagesBySession[editingSession.id] || []).map(img => {
                  const src = img.download_url || img.url;
                  const isPdf = (img.filename || '').toLowerCase().endsWith('.pdf') || (src || '').toLowerCase().endsWith('.pdf');
                  return (
                    <div key={img.id} className="relative group">
                      {isPdf ? (
                        <PdfThumbnail
                          fileId={img.id}
                          url={src}
                          thumbnailUrl={img.thumbnail_url}
                          filename={img.filename}
                          sizeClass="w-16 h-16"
                          onClick={() => setFilePreview({ id: img.id, url: src, filename: img.filename, thumbnailUrl: img.thumbnail_url })}
                        />
                      ) : (
                        (() => {
                          const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((img.filename || ''));
                          const thumb = isImg ? src : (img.thumbnail_url || src);
                          return (
                            <img src={thumb} alt={img.filename} className="w-16 h-16 object-cover rounded shadow cursor-pointer" onClick={() => setFilePreview({ id: img.id, url: src, filename: img.filename, thumbnailUrl: img.thumbnail_url })} />
                          );
                        })()
                      )}
                      <button className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full px-1.5 text-xs opacity-0 group-hover:opacity-100" onClick={() => removeSessionImage(editingSession.id, img.id)}>×</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sessions List */}
      <div className="mt-8">
        <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Recorded Sessions</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Group by</span>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="input text-sm w-40"
            >
              <option value="date">Date</option>
              <option value="recipe">Recipe</option>
            </select>
          </div>
        </div>
        <div className="space-y-4">
          {groupedSessions.map((group) => {
            const collapsed = collapsedGroups[group.key] ?? true;
            return (
              <div key={group.key} className="border border-gray-200 rounded-lg bg-white shadow-sm">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                  onClick={() => toggleGroup(group.key)}
                >
                  <div>
                    <p className="font-semibold text-gray-900">{group.title}</p>
                    {group.subtitle && <p className="text-sm text-gray-500">{group.subtitle}</p>}
                    <p className="text-xs text-gray-400 mt-0.5">
                      {group.sessions.length} session{group.sessions.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${collapsed ? '' : 'rotate-180'}`}
                  />
                </button>
                {!collapsed && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-b-lg">
                    {group.sessions.map((session) => renderSessionCard(session))}
                  </div>
                )}
              </div>
            );
          })}

          {groupedSessions.length === 0 && (
            <div className="text-center py-12">
              <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No firing sessions recorded yet</p>
              <button
                onClick={() => setShowForm(true)}
                className="btn btn-primary mt-4"
              >
                Record Your First Session
              </button>
            </div>
          )}
        </div>
      </div>
      <ImagePreviewModal id={filePreview?.id} url={filePreview?.url} filename={filePreview?.filename} thumbnailUrl={filePreview?.thumbnailUrl} onClose={() => setFilePreview(null)} />
    </div>
  );
};

export default FiringSessions;